/**
 * Utilities for image download functionality
 */

/**
 * Check if URL is a valid image URL
 * @param {string} url - URL to validate
 * @returns {boolean} True if valid image URL
 */
export const isValidImageUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  try {
    const urlObj = new URL(url);
    return urlObj.protocol === "http:" || urlObj.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Extract preview images from current page data
 * @param {Array} data - Array of row data
 * @param {Array} selectedColumns - Selected image columns
 * @param {number} maxCount - Maximum number of images to return
 * @returns {Array} Array of preview image objects
 */
export const extractPreviewImages = (data, selectedColumns, maxCount = 6) => {
  const images = [];

  if (!data || !selectedColumns || selectedColumns.length === 0) {
    return images;
  }

  for (const row of data) {
    if (images.length >= maxCount) break;

    for (const column of selectedColumns) {
      if (images.length >= maxCount) break;

      const url = row.data?.[column.id];
      if (url && isValidImageUrl(url)) {
        images.push({
          url,
          columnName: column.name,
          rowIndex: row.sequence_number || data.indexOf(row),
        });
      }
    }
  }

  return images;
};

/**
 * Sanitize string for use as filename
 * @param {string} str - Input string
 * @returns {string} Sanitized filename
 */
export const sanitizeFilename = (str) => {
  return String(str || "")
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .substring(0, 50)
    .trim();
};

/**
 * Generate a preview filename to show the user
 * @param {string} template - Naming template
 * @param {Object} sampleRow - Sample row data
 * @param {string} columnName - Image column name
 * @param {Array} columns - All columns
 * @returns {string} Preview filename
 */
export const generatePreviewFilename = (template, sampleRow, columnName, columns) => {
  let filename = template
    .replace(/{column_name}/gi, sanitizeFilename(columnName))
    .replace(/{index}/gi, "0001")
    .replace(/{row_index}/gi, "0001");

  // Replace dynamic placeholders from row data
  const placeholderRegex = /{([^}]+)}/g;
  const rowData = sampleRow?.data || {};

  filename = filename.replace(placeholderRegex, (match, key) => {
    // Try to find column by name (case-insensitive)
    const column = columns.find(
      (col) => col.name.toLowerCase() === key.toLowerCase() || col.id === key
    );

    if (column) {
      const value = rowData?.[column.id];
      if (value && typeof value === "string") {
        return sanitizeFilename(value).substring(0, 30);
      }
    }

    // Return original placeholder if not found
    return match;
  });

  // Ensure extension
  if (!filename.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|avif)$/i)) {
    filename += ".jpg";
  }

  return filename;
};

/**
 * Calculate estimated image count based on sample data
 * @param {Array} sampleData - Sample data from current page
 * @param {Array} selectedColumns - Selected image columns
 * @param {number} totalRows - Total row count
 * @returns {number} Estimated total image count
 */
export const calculateEstimatedImageCount = (sampleData, selectedColumns, totalRows) => {
  if (!sampleData?.length || !selectedColumns?.length || !totalRows) return 0;

  // Count non-empty image cells in sample
  let sampleImageCount = 0;
  for (const row of sampleData) {
    for (const column of selectedColumns) {
      const url = row.data?.[column.id];
      if (url && isValidImageUrl(url)) {
        sampleImageCount++;
      }
    }
  }

  // Calculate fill rate from sample
  const maxPossible = sampleData.length * selectedColumns.length;
  if (maxPossible === 0) return 0;

  const fillRate = sampleImageCount / maxPossible;

  // Extrapolate to total rows
  return Math.round(fillRate * totalRows * selectedColumns.length);
};

/**
 * Get available placeholders for naming template
 * @param {Array} columns - All table columns
 * @returns {Array} Array of placeholder objects
 */
export const getAvailablePlaceholders = (columns) => {
  const placeholders = [
    { key: "{column_name}", label: "Column Name", description: "Name of the image column" },
    { key: "{index}", label: "Index", description: "Global sequential number (0001, 0002...)" },
    { key: "{row_index}", label: "Row Index", description: "Row number in table" },
  ];

  // Add non-image columns as dynamic placeholders
  const nonImageColumns = columns.filter((col) => col.type !== "image");
  for (const col of nonImageColumns.slice(0, 10)) {
    placeholders.push({
      key: `{${col.name}}`,
      label: col.name,
      description: `Value from "${col.name}" column`,
      dynamic: true,
    });
  }

  return placeholders;
};
